import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Flex,
  Heading,
  Text,
  Button,
  Separator,
} from "@radix-ui/themes";
import {
  CopyIcon,
  DownloadIcon,
  UpdateIcon,
  PlusIcon,
} from "@radix-ui/react-icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "sonner";
import { Dialog } from "@/components/shared/dialog";
import { FeatureAccessContext } from "@/context/featureAccess";
import { useTenantApiKeyApi } from "@/v2/entities/tenant-api-key/TenantApiKey.api";
import type { TenantApiKey } from "@/v2/entities/tenant-api-key/TenantApiKey.model";
import styles from "./ApiKeys.module.scss";

const QUERY_KEY = "tenant-api-keys";

function maskApiKey(key: string): string {
  if (key.length <= 16) return key;
  return `${key.slice(0, 12)}...${key.slice(-4)}`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ApiKeys() {
  const apiKeyApi = useTenantApiKeyApi();
  const queryClient = useQueryClient();
  const { selectedWorkspace } = useContext(FeatureAccessContext);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [newlyGeneratedKey, setNewlyGeneratedKey] = useState<string | null>(
    null,
  );

  // the page stays mounted when switching workspace, so drop the previous workspace's full key
  useEffect(() => {
    setNewlyGeneratedKey(null);
  }, [selectedWorkspace]);

  const { data, isLoading } = useQuery({
    queryKey: [QUERY_KEY, selectedWorkspace],
    queryFn: () => apiKeyApi.getAll(),
    staleTime: 60_000,
  });

  const exploreKey = useMemo<TenantApiKey | undefined>(
    () => data?.apiKeys.find((k) => k.type === "explore"),
    [data],
  );

  const generateMutation = useMutation({
    mutationFn: () => apiKeyApi.generate({ type: "explore" }),
    onSuccess: (result) => {
      setNewlyGeneratedKey(result.apiKey);
      queryClient.invalidateQueries([QUERY_KEY]);
      toast.success("API key generated successfully");
    },
    onError: () => {
      toast.error("Failed to generate API key. Please try again.");
    },
  });

  const handleGenerate = useCallback(() => {
    setNewlyGeneratedKey(null);
    generateMutation.mutate();
  }, [generateMutation]);

  const handleRegenerateClick = useCallback(() => {
    setConfirmOpen(true);
  }, []);

  const handleConfirmRegenerate = useCallback(() => {
    setConfirmOpen(false);
    setNewlyGeneratedKey(null);
    generateMutation.mutate();
  }, [generateMutation]);

  const handleCopyKey = useCallback(
    (key: string) => {
      navigator.clipboard.writeText(key).then(
        () => toast.success("API key copied to clipboard"),
        () => toast.error("Failed to copy to clipboard"),
      );
    },
    [],
  );

  const [isCopyingPrompt, setIsCopyingPrompt] = useState(false);

  const handleCopyPrompt = useCallback(async () => {
    setIsCopyingPrompt(true);
    try {
      const markdown = await apiKeyApi.getExploreDocs();
      const prompt = [
        "You are helping me integrate with the Quack Explore API.",
        "Use the API reference below to help me write code, authenticate, and query my workspace data.",
        "Authenticate every request with the `x-explore-api-key` header using my Explore API key (generate one in Quack under Settings → API Keys).",
        "",
        "--- QUACK EXPLORE API DOCUMENTATION ---",
        markdown,
      ].join("\n");
      await navigator.clipboard.writeText(prompt);
      toast.success("Prompt copied to clipboard");
    } catch {
      toast.error("Failed to copy prompt. Please try again.");
    } finally {
      setIsCopyingPrompt(false);
    }
  }, [apiKeyApi]);

  const handleDownloadDocs = useCallback(async () => {
    try {
      const markdown = await apiKeyApi.getExploreDocs();
      const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "explore-api-docs.md";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("API docs downloaded");
    } catch {
      toast.error("Failed to download API docs. Please try again.");
    }
  }, [apiKeyApi]);

  const handleConfirmOpenChange = useCallback((open: boolean) => {
    setConfirmOpen(open);
  }, []);

  if (isLoading) {
    return (
      <Box className={styles.container}>
        <Flex direction="column" gap="2">
          <Heading size="6">API Keys</Heading>
          <Text color="gray" size="3">
            Loading...
          </Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Flex direction="column" gap="5">
        <Flex direction="column" gap="2">
          <Heading size="6">API Keys</Heading>
          <Text color="gray" size="3">
            Manage API keys for programmatic access to your workspace
          </Text>
        </Flex>

        <Card>
          <Flex direction="column" gap="4">
            <Flex direction="column" gap="1">
              <Text size="3" weight="bold">
                Explore API Key
              </Text>
              <Text size="2" color="gray">
                Use this key to access the Explore API without user
                authentication
              </Text>
            </Flex>

            <Separator />

            {newlyGeneratedKey && (
              <Flex direction="column" gap="2" className={styles.newKeyBanner}>
                <Text size="2" weight="bold" color="green">
                  New API key generated
                </Text>
                <Text size="1" color="gray">
                  Copy this key now. You will not be able to see the full key
                  again.
                </Text>
                <Flex align="center" gap="2">
                  <Text className={styles.newKeyValue}>
                    {newlyGeneratedKey}
                  </Text>
                  <Button
                    variant="soft"
                    size="1"
                    color="gray"
                    highContrast
                    onClick={() => handleCopyKey(newlyGeneratedKey)}
                  >
                    <CopyIcon />
                    Copy
                  </Button>
                </Flex>
              </Flex>
            )}

            {exploreKey ? (
              <Flex direction="column" gap="3">
                <Flex align="center" gap="2">
                  <Text className={styles.keyValue}>
                    {maskApiKey(exploreKey.apiKey)}
                  </Text>
                  <Button
                    variant="soft"
                    size="1"
                    color="gray"
                    highContrast
                    onClick={() => handleCopyKey(exploreKey.apiKey)}
                  >
                    <CopyIcon />
                    Copy
                  </Button>
                </Flex>

                <Flex gap="4">
                  <Text size="1" color="gray">
                    Created: {formatDate(exploreKey.createdAt)}
                  </Text>
                  <Text size="1" color="gray">
                    Updated: {formatDate(exploreKey.updatedAt)}
                  </Text>
                </Flex>

                <Flex>
                  <Button
                    variant="outline"
                    size="2"
                    color="red"
                    onClick={handleRegenerateClick}
                    disabled={generateMutation.isLoading}
                  >
                    <UpdateIcon />
                    Regenerate Key
                  </Button>
                </Flex>
              </Flex>
            ) : (
              <Flex>
                <Button
                  variant="solid"
                  size="2"
                  onClick={handleGenerate}
                  disabled={generateMutation.isLoading}
                >
                  <PlusIcon />
                  Generate Key
                </Button>
              </Flex>
            )}
          </Flex>
        </Card>

        <Card>
          <Flex direction="column" gap="4">
            <Flex direction="column" gap="1">
              <Text size="3" weight="bold">
                API Documentation
              </Text>
              <Text size="2" color="gray">
                Download the full Explore API reference, or copy a ready-to-use
                prompt to integrate with the help of an AI assistant
              </Text>
            </Flex>

            <Separator />

            <Flex gap="3" wrap="wrap">
              <Button
                variant="outline"
                size="2"
                color="gray"
                highContrast
                onClick={handleDownloadDocs}
              >
                <DownloadIcon />
                Download Explore API Docs
              </Button>
              <Button
                variant="outline"
                size="2"
                color="gray"
                highContrast
                onClick={handleCopyPrompt}
                disabled={isCopyingPrompt}
              >
                <CopyIcon />
                {isCopyingPrompt ? "Copying..." : "Copy prompt"}
              </Button>
            </Flex>
          </Flex>
        </Card>
      </Flex>

      <Dialog open={confirmOpen} onOpenChange={handleConfirmOpenChange}>
        <Dialog.Content size="small">
          <Flex direction="column" gap="4" p="4">
            <Dialog.Title>Regenerate API Key?</Dialog.Title>
            <Dialog.Close />
            <Text size="2" color="gray">
              This will invalidate your current Explore API key. Any
              integrations using the old key will stop working immediately.
            </Text>
            <Flex gap="3" justify="end">
              <Button
                variant="soft"
                color="gray"
                size="2"
                onClick={() => handleConfirmOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                color="red"
                size="2"
                onClick={handleConfirmRegenerate}
                disabled={generateMutation.isLoading}
              >
                Regenerate
              </Button>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog>
    </Box>
  );
}
